import React from 'react';
import Button from '../common/Button';

const TimeSlotGrid = ({ timeSlots, onSlotSelect, selectedDate }) => {
  const getSlotStatus = (slot) => {
    if (slot.is_blocked) {
      return {
        status: 'blocked',
        className: 'bg-gray-100 text-gray-500 cursor-not-allowed',
        label: slot.blocked_reason || 'Blocked'
      };
    }
    
    if (slot.reservation_id) {
      return {
        status: 'reserved',
        className: 'bg-red-100 text-red-700 cursor-not-allowed',
        label: slot.reservation_status === 'checked_in' ? 'In Use' : 'Reserved'
      };
    }
    
    const slotTime = new Date(slot.start_time);
    const now = new Date();
    
    if (slotTime < now) {
      return {
        status: 'past',
        className: 'bg-gray-100 text-gray-400 cursor-not-allowed',
        label: 'Past'
      };
    }
    
    return {
      status: 'available',
      className: 'bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer',
      label: 'Available'
    };
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const groupSlotsByHour = (slots) => {
    const groups = {};
    slots.forEach(slot => {
      const hour = new Date(slot.start_time).getHours();
      if (!groups[hour]) {
        groups[hour] = [];
      }
      groups[hour].push(slot);
    });
    return groups;
  };

  if (!timeSlots || timeSlots.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          No time slots available
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          There are no time slots configured for this date.
        </p>
      </div>
    );
  }

  const groupedSlots = groupSlotsByHour(timeSlots);
  const hours = Object.keys(groupedSlots).sort((a, b) => parseInt(a) - parseInt(b));

  return (
    <div className="space-y-6">
      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center">
          <div className="w-4 h-4 bg-green-100 border border-green-200 rounded mr-2"></div>
          <span className="text-green-700">Available</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-red-100 border border-red-200 rounded mr-2"></div>
          <span className="text-red-700">Reserved</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-gray-100 border border-gray-200 rounded mr-2"></div>
          <span className="text-gray-500">Blocked/Past</span>
        </div>
      </div>

      {/* Time Slots */}
      <div className="grid gap-6">
        {hours.map(hour => {
          const hourSlots = groupedSlots[hour];
          const displayHour = parseInt(hour) === 0 ? 12 : 
                             parseInt(hour) > 12 ? parseInt(hour) - 12 : parseInt(hour);
          const ampm = parseInt(hour) >= 12 ? 'PM' : 'AM';
          
          return (
            <div key={hour} className="border-b border-gray-200 pb-6 last:border-b-0">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {displayHour}:00 {ampm}
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {hourSlots.map(slot => {
                  const slotInfo = getSlotStatus(slot);
                  
                  return (
                    <div
                      key={slot.id}
                      className={`p-4 rounded-lg border transition-colors duration-200 ${
                        slotInfo.className
                      }`}
                      onClick={() => {
                        if (slotInfo.status === 'available') {
                          onSlotSelect(slot);
                        }
                      }}
                    >
                      <div className="text-sm font-medium">
                        {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                      </div>
                      
                      <div className="text-xs mt-1">
                        {slotInfo.label}
                      </div>
                      
                      {/* Show reserved by info if applicable */}
                      {slot.reservation_id && slot.reserved_by_first_name && (
                        <div className="text-xs text-gray-600 mt-1">
                          Reserved by {slot.reserved_by_first_name} {slot.reserved_by_last_name}
                        </div>
                      )}
                      
                      {/* Show blocked reason if applicable */}
                      {slot.is_blocked && slot.blocked_reason && (
                        <div className="text-xs text-gray-600 mt-1">
                          {slot.blocked_reason}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Reserve Actions */}
      <div className="bg-blue-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">
          Quick Reserve
        </h4>
        <div className="flex flex-wrap gap-2">
          {timeSlots
            .filter(slot => getSlotStatus(slot).status === 'available')
            .slice(0, 3)
            .map(slot => (
              <Button
                key={slot.id}
                onClick={() => onSlotSelect(slot)}
                size="small"
                variant="outline"
                className="text-blue-700 border-blue-300 hover:bg-blue-100"
              >
                {formatTime(slot.start_time)}
              </Button>
            ))
          }
          {timeSlots.filter(slot => getSlotStatus(slot).status === 'available').length > 3 && (
            <span className="text-xs text-blue-600 self-center">
              +{timeSlots.filter(slot => getSlotStatus(slot).status === 'available').length - 3} more
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeSlotGrid;